/**
 * Risk Capital -- FI-tenant Risk Capital Reserve (CRT) page.
 *
 * Sprint C-CRT-01.
 *
 * Surfaces the FI's CRT position (total / available / locked) and the two
 * institutional cash-management flows that move Risk Capital across the
 * Aaim network:
 *
 *  - Subscribe: a 3-step wizard. The FI requests a Risk Capital allocation
 *    by amount; Aaim issues wire instructions plus a funding reference
 *    that the FI's wire room places on the outbound wire to the Custodian
 *    FI. Once the Custodian FI HMAC-signs the wire-received callback to
 *    Aaim, the subscription transitions PENDING -> FUNDING_PENDING ->
 *    FUNDED. The pending list polls GET /api/v1/crt/subscriptions/{id}
 *    with ETag-aware caching for status updates.
 *
 *  - Redeem: placeholder. The full redemption symmetry lands in
 *    sprint-c-crt-03; this tab keeps a stub so navigation is stable.
 *
 * Vocabulary mandate (per sprint brief): the page uses Subscribe,
 * Redeem, Confirmation, and Custodian FI exclusively in operator-facing
 * copy. Internal field names (`fundedAttestationId`) survive in the
 * audit drawer with the label "Confirmation #". See
 * sprint-queue/sprint-c-crt-01-subscription-flow-and-custodian-config/
 * README.md for the full mandate table.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowDownToLine, Copy } from 'lucide-react';
import {
  Card,
  Heading,
  MetricCard,
  MetricCardGrid,
  Skeleton,
  Button,
  Badge,
  Input,
  Select,
  FormLabel,
  FormHelperText,
  Tabs,
  Dialog,
  TableScroll,
} from '@aaimplatform/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as CrtComponents } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import { cooperativeReserveTokenClient } from '../../api/client';
import { formatCurrencyFull, formatCurrencyCompact as formatCurrency } from './format';

type ParticipantPosition = CrtComponents['schemas']['ParticipantPosition'];
type SubscriptionDto = CrtComponents['schemas']['SubscriptionDto'];
type SubscriptionStatus = CrtComponents['schemas']['SubscriptionStatus'];
type CustodianConfigDto = CrtComponents['schemas']['CustodianConfigDto'];

function formatAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '--';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

const STATUS_VARIANT: Record<SubscriptionStatus, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  PENDING: 'info',
  FUNDING_PENDING: 'warning',
  FUNDED: 'success',
  MINT_PENDING: 'warning',
  COMPLETED: 'success',
  // term-register-skip: enum key mirrors the SubscriptionStatus value CANCELLED in cooperative-reserve-token.openapi.yaml:1483; an identifier, not prose
  CANCELLED: 'neutral',
  VALIDATION_ERROR: 'error',
};

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  PENDING: 'Awaiting wire',
  FUNDING_PENDING: 'Wire observed',
  FUNDED: 'Funded',
  MINT_PENDING: 'Crediting position',
  COMPLETED: 'Completed',
  // term-register-skip: enum key mirrors the SubscriptionStatus value CANCELLED in cooperative-reserve-token.openapi.yaml:1483; an identifier, not prose
  CANCELLED: 'Canceled',
  VALIDATION_ERROR: 'Needs reconciliation',
};

// ---------------------------------------------------------------------------
// Position hook
//
// Uses the list endpoint `/api/v1/crt/positions` (NOT the path-param
// `/positions/{participantId}` form). Rationale:
//
//  1. The backend list handler is tenant-scoped server-side via
//     resolveEffectiveTenantId(req) -- it returns only the caller's tenant's
//     CRT positions regardless of any query parameters. See
//     services/cooperative-reserve-token/src/routes/positions.routes.ts:29.
//
//  2. The path-param form requires a CRT participant row to exist for
//     {tenantId, participantId}, otherwise returns 404. A fresh FI tenant
//     with no CRT subscription yet hits this 404 on every page load.
//
//  3. The path-param form also requires participantId to be a UUID. Platform
//     admin SSO tokens carry tenantId="apex" (string literal) which the
//     Prisma WHERE clause rejects with an "invalid input syntax for type uuid"
//     500. The list form is unaffected by this entire class of bug.
//
// The page renders a clean zero-balance state when positions[] is empty,
// which is the correct UX for a tenant that has not yet subscribed.
// ---------------------------------------------------------------------------

function useParticipantPosition() {
  return useQuery({
    queryKey: ['crt-position-mine'],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/positions',
        {
          params: { query: { page: 1, pageSize: 1 } },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ??
            'Failed to load Risk Capital position',
        );
      }
      const positions = (data?.positions ?? []) as ParticipantPosition[];
      return positions[0] ?? null;
    },
    staleTime: 30 * 1000,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Active custodian wire instructions hook
// ---------------------------------------------------------------------------

function useActiveCustodianConfig() {
  return useQuery({
    queryKey: ['crt-custodian-config-active'],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/custodian-config',
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ??
            'Failed to load custodian wire instructions',
        );
      }
      const list = (data?.configs ?? []) as CustodianConfigDto[];
      const active = list.find((c) => c.active) ?? list[0];
      return active ?? null;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Subscriptions list hook (FI-tenant view)
// ---------------------------------------------------------------------------

const OPEN_STATUSES: SubscriptionStatus[] = ['PENDING', 'FUNDING_PENDING', 'MINT_PENDING'];

function useSubscriptions() {
  return useQuery({
    queryKey: ['crt-subscriptions'],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/subscriptions',
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ??
            'Failed to load subscriptions',
        );
      }
      return (data?.subscriptions ?? []) as SubscriptionDto[];
    },
    // Poll every 5s while open subscriptions exist, otherwise every 30s.
    refetchInterval: (query) => {
      const subs = (query.state.data as SubscriptionDto[] | undefined) ?? [];
      const hasOpen = subs.some((s) => OPEN_STATUSES.includes(s.status));
      return hasOpen ? 5000 : 30000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Subscribe wizard
// ---------------------------------------------------------------------------

type WizardStep = 'amount' | 'review' | 'submitted';

interface SubscribeState {
  amount: string;
  currencyCode: string;
  internalNote: string;
  step: WizardStep;
  submitted: SubscriptionDto | null;
}

function SubscribeWizard() {
  const queryClient = useQueryClient();
  const { data: custodianConfig, isLoading: configLoading, error: configError } =
    useActiveCustodianConfig();

  const [state, setState] = useState<SubscribeState>({
    amount: '',
    currencyCode: 'USD',
    internalNote: '',
    step: 'amount',
    submitted: null,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const amount = Number(state.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Amount must be a positive number');
      }
      const { data, error } = await cooperativeReserveTokenClient.POST(
        '/api/v1/crt/subscriptions',
        {
          body: {
            amount,
            currencyCode: state.currencyCode,
            internalNote: state.internalNote.trim() || undefined,
            custodianConfigId: custodianConfig?.configId,
          },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Subscription failed',
        );
      }
      return data as SubscriptionDto;
    },
    onSuccess: (data) => {
      setState((s) => ({ ...s, step: 'submitted', submitted: data }));
      void queryClient.invalidateQueries({ queryKey: ['crt-subscriptions'] });
    },
  });

  const numericAmount = Number(state.amount);
  const canSubmit =
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    !submitMutation.isPending &&
    !!custodianConfig;

  function reset() {
    setState({
      amount: '',
      currencyCode: 'USD',
      internalNote: '',
      step: 'amount',
      submitted: null,
    });
    submitMutation.reset();
  }

  function goReview() {
    if (canSubmit) setState((s) => ({ ...s, step: 'review' }));
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-canvas/50">
        <div className="text-xs font-sans text-secondary leading-relaxed">
          <strong>How Subscribe works:</strong>{' '}
          (1) Confirm your subscription amount. (2) Aaim issues wire
          instructions and a unique Funding Reference. (3) Place the wire
          to the Custodian FI with the Funding Reference on the memo line.
          Once the wire arrives the system credits your position
          automatically.
        </div>
      </Card>

      {configError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
          <div className="text-xs font-sans text-error">
            {configError instanceof Error
              ? configError.message
              : 'Failed to load custodian wire instructions'}
          </div>
        </div>
      )}

      {state.step === 'amount' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel htmlFor="subscribe-amount" required>
              Amount (USD)
            </FormLabel>
            <Input
              id="subscribe-amount"
              type="number"
              value={state.amount}
              onChange={(e) =>
                setState((s) => ({ ...s, amount: e.target.value }))
              }
              min={1}
              step={1000}
              placeholder="100000"
            />
            <FormHelperText>
              Risk Capital position credit: 1 CRT per 1 USD subscribed.
            </FormHelperText>
          </div>
          <div>
            <FormLabel htmlFor="subscribe-currency">Currency</FormLabel>
            <Select
              id="subscribe-currency"
              value={state.currencyCode}
              onChange={(e) =>
                setState((s) => ({ ...s, currencyCode: e.target.value }))
              }
              options={[{ value: 'USD', label: 'USD' }]}
            />
          </div>
          <div className="md:col-span-2">
            <FormLabel htmlFor="subscribe-note">
              Internal note (optional)
            </FormLabel>
            <Input
              id="subscribe-note"
              type="text"
              value={state.internalNote}
              onChange={(e) =>
                setState((s) => ({ ...s, internalNote: e.target.value }))
              }
              placeholder="Internal notes (not sent to the Custodian FI)"
            />
          </div>
          <div className="md:col-span-2 flex justify-end pt-2">
            <Button
              variant="primary"
              icon={ArrowDownToLine}
              iconPosition="left"
              disabled={!canSubmit || configLoading}
              onClick={goReview}
            >
              Review wire instructions
            </Button>
          </div>
        </div>
      )}

      {state.step === 'review' && custodianConfig && (
        <div className="space-y-4">
          <Card className="p-4">
            <Heading level={3} size="card" className="mb-3">
              Custodian FI wire instructions
            </Heading>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm font-sans">
              <div>
                <dt className="text-xs text-tertiary">Beneficiary bank</dt>
                <dd className="text-primary">{custodianConfig.bankName}</dd>
              </div>
              <div>
                <dt className="text-xs text-tertiary">Beneficiary account</dt>
                <dd className="text-primary">{custodianConfig.accountName}</dd>
              </div>
              <div>
                <dt className="text-xs text-tertiary">Routing number</dt>
                <dd className="font-mono tabular-nums text-primary">
                  {custodianConfig.routingNumber}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-tertiary">Account number</dt>
                <dd className="font-mono tabular-nums text-primary">
                  {custodianConfig.accountNumberMasked}
                </dd>
              </div>
              {custodianConfig.swiftCode && (
                <div>
                  <dt className="text-xs text-tertiary">SWIFT / BIC</dt>
                  <dd className="font-mono tabular-nums text-primary">
                    {custodianConfig.swiftCode}
                  </dd>
                </div>
              )}
              {custodianConfig.fedwireObi && (
                <div>
                  <dt className="text-xs text-tertiary">
                    Fedwire OBI (memo line)
                  </dt>
                  <dd className="text-primary">{custodianConfig.fedwireObi}</dd>
                </div>
              )}
            </dl>
          </Card>

          <Card className="p-4 bg-canvas/50">
            <div className="text-xs font-sans text-secondary leading-relaxed">
              Amount: <strong>{formatCurrencyFull(numericAmount)}</strong>{' '}
              {state.currencyCode}. The Funding Reference will be issued when
              you submit; place it on the wire memo line so the Custodian FI
              can match the deposit to this subscription.
            </div>
          </Card>

          {submitMutation.isError && (
            <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
              <div className="text-xs font-sans text-error">
                {submitMutation.error instanceof Error
                  ? submitMutation.error.message
                  : 'Subscription failed'}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setState((s) => ({ ...s, step: 'amount' }))}
              disabled={submitMutation.isPending}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={() => submitMutation.mutate()}
              disabled={!canSubmit}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit subscription'}
            </Button>
          </div>
        </div>
      )}

      {state.step === 'submitted' && state.submitted && (
        <div className="space-y-3">
          <Card className="p-4 bg-growth/10 border border-growth/30">
            <div className="text-sm font-sans text-secondary leading-relaxed">
              <strong>Subscription submitted.</strong>{' '}
              Place the wire with the Funding Reference below on the memo
              line. The page will update automatically once the Custodian
              FI confirms wire receipt.
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-tertiary mb-1">
                  Funding Reference
                </div>
                <div className="font-mono tabular-nums text-lg text-primary">
                  {state.submitted.fundingReference.value}
                </div>
              </div>
              <Button
                variant="secondary"
                icon={Copy}
                iconPosition="left"
                onClick={() =>
                  void navigator.clipboard.writeText(
                    state.submitted!.fundingReference.value,
                  )
                }
              >
                Copy
              </Button>
            </div>
          </Card>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={reset}>
              Start another subscription
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redeem placeholder
// ---------------------------------------------------------------------------

function RedeemPlaceholder() {
  return (
    <Card className="p-6 bg-canvas/50">
      <div className="text-sm font-sans text-secondary leading-relaxed">
        <strong>Redeem flow lands in sprint-c-crt-03.</strong>{' '}
        Until then, redemption requests are handled out-of-band through
        Aaim Operations. The Subscribe tab is fully wired.
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pending Subscriptions list
// ---------------------------------------------------------------------------

function PendingSubscriptionsList({
  subscriptions,
  onOpenDetail,
}: {
  subscriptions: SubscriptionDto[];
  onOpenDetail: (sub: SubscriptionDto) => void;
}) {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await cooperativeReserveTokenClient.POST(
        '/api/v1/crt/subscriptions/{requestId}/cancel',
        {
          params: { path: { requestId } },
          body: { cancelReason: 'Canceled by operator' },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Cancel failed',
        );
      }
      return data as SubscriptionDto;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crt-subscriptions'] });
    },
  });

  if (subscriptions.length === 0) {
    return (
      <Card className="p-6 bg-canvas/50">
        <div className="text-sm font-sans text-tertiary">
          No open subscriptions.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden">
      <TableScroll label="Open subscriptions">
      <table className="w-full min-w-table-dense text-sm font-sans">
        <thead className="bg-canvas/50 border-b border-recessed">
          <tr>
            <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
              Funding Reference
            </th>
            <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
              Amount
            </th>
            <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
              Status
            </th>
            <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
              Age
            </th>
            <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map((sub) => (
            <tr
              key={sub.requestId}
              className="border-b border-recessed last:border-b-0"
            >
              <td className="px-4 py-3 font-mono tabular-nums text-primary">
                {sub.fundingReference.value}
              </td>
              <td className="px-4 py-3 text-right text-primary">
                {formatCurrencyFull(sub.amount)}
              </td>
              <td className="px-4 py-3">
                <Badge variant={STATUS_VARIANT[sub.status]} size="sm">
                  {STATUS_LABEL[sub.status]}
                </Badge>
              </td>
              <td className="px-4 py-3 text-tertiary">
                {formatAge(sub.requestedAt)}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onOpenDetail(sub)}
                  >
                    Details
                  </Button>
                  {sub.status === 'PENDING' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => cancelMutation.mutate(sub.requestId)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer (Dialog)
// ---------------------------------------------------------------------------

function SubscriptionDetailDialog({
  subscription,
  onClose,
}: {
  subscription: SubscriptionDto | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={subscription !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Subscription detail"
      size="lg"
    >
      {subscription && (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm font-sans">
          <div>
            <dt className="text-xs text-tertiary">Funding Reference</dt>
            <dd className="font-mono tabular-nums text-primary">
              {subscription.fundingReference.value}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-tertiary">Status</dt>
            <dd>
              <Badge variant={STATUS_VARIANT[subscription.status]} size="sm">
                {STATUS_LABEL[subscription.status]}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-tertiary">Amount</dt>
            <dd className="text-primary">
              {formatCurrencyFull(subscription.amount)}{' '}
              {subscription.currencyCode}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-tertiary">Requested at</dt>
            <dd className="text-primary">
              {new Date(subscription.requestedAt).toLocaleString()}
            </dd>
          </div>
          {subscription.fundedAt && (
            <div>
              <dt className="text-xs text-tertiary">Funded at</dt>
              <dd className="text-primary">
                {new Date(subscription.fundedAt).toLocaleString()}
              </dd>
            </div>
          )}
          {subscription.cancelledAt && (
            <div>
              <dt className="text-xs text-tertiary">Canceled at</dt>
              <dd className="text-primary">
                {new Date(subscription.cancelledAt).toLocaleString()}
              </dd>
            </div>
          )}
          {subscription.cancelReason && (
            <div className="md:col-span-2">
              <dt className="text-xs text-tertiary">Cancel reason</dt>
              <dd className="text-primary">{subscription.cancelReason}</dd>
            </div>
          )}
          {subscription.custodianReferenceId && (
            <div>
              <dt className="text-xs text-tertiary">
                Custodian reference id
              </dt>
              <dd className="font-mono tabular-nums text-primary">
                {subscription.custodianReferenceId}
              </dd>
            </div>
          )}
          {subscription.fundedAttestationId && (
            <div>
              <dt className="text-xs text-tertiary">Confirmation #</dt>
              <dd className="font-mono tabular-nums text-primary">
                {subscription.fundedAttestationId}
              </dd>
            </div>
          )}
          {subscription.mintTransactionId && (
            <div>
              <dt className="text-xs text-tertiary">Position credit id</dt>
              <dd className="font-mono tabular-nums text-primary">
                {subscription.mintTransactionId}
              </dd>
            </div>
          )}
        </dl>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RiskCapital() {
  // The list endpoint resolves the effective tenant from req.user server-side
  // (resolveEffectiveTenantId) so no client-side participantId derivation is
  // needed -- the prior `user?.tenantId` derivation produced a string-literal
  // "apex" for platform-admin SSO tokens which the path-param form rejected
  // as non-UUID.
  const { data: position, isLoading, error } = useParticipantPosition();

  const { data: subscriptions = [] } = useSubscriptions();

  const [activeTab, setActiveTab] = useState<'subscribe' | 'redeem'>('subscribe');
  const [detailSub, setDetailSub] = useState<SubscriptionDto | null>(null);

  // Reset detail dialog if the subscription is no longer in the list.
  useEffect(() => {
    if (!detailSub) return;
    const current = subscriptions.find(
      (s) => s.requestId === detailSub.requestId,
    );
    if (current && current !== detailSub) {
      setDetailSub(current);
    }
  }, [subscriptions, detailSub]);

  const total = position?.totalBalance ?? 0;
  const available = position?.availableBalance ?? 0;
  const locked = position?.lockedBalance ?? 0;
  const interestEarned = position?.totalInterestEarned ?? 0;
  const activeParticipations = position?.activeParticipations ?? 0;

  const lockedShare = useMemo(() => {
    if (total === 0) return 0;
    return locked / total;
  }, [total, locked]);

  const openSubs = useMemo(
    () => subscriptions.filter((s) => OPEN_STATUSES.includes(s.status)),
    [subscriptions],
  );
  const historicalSubs = useMemo(
    () =>
      subscriptions.filter(
        (s) =>
          s.status === 'FUNDED' ||
          s.status === 'COMPLETED' ||
          // term-register-skip: compares against the SubscriptionStatus value CANCELLED in cooperative-reserve-token.openapi.yaml:1483; an identifier, not prose
          s.status === 'CANCELLED' ||
          s.status === 'VALIDATION_ERROR',
      ),
    [subscriptions],
  );

  const tabs = [
    { id: 'subscribe', label: 'Subscribe' },
    { id: 'redeem', label: 'Redeem' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Subscribe to or redeem Risk Capital across the Aaim network.
            The Custodian FI in the participant network holds the
            corresponding cash 1:1 with your CRT position.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load Risk Capital position:{' '}
            {error instanceof Error ? error.message : 'unknown error'}
          </div>
        </div>
      )}

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))
        ) : (
          <>
            <MetricCard
              label="Total CRT"
              value={formatCurrency(total)}
              change={formatCurrencyFull(total)}
              changeType="neutral"
            />
            <MetricCard
              label="Available"
              value={formatCurrency(available)}
              change="Free to commit or redeem"
              changeType={available > 0 ? 'positive' : 'neutral'}
            />
            <MetricCard
              label="Locked"
              value={formatCurrency(locked)}
              change={
                lockedShare > 0
                  ? `${(lockedShare * 100).toFixed(1)}% in active participations`
                  : 'No active locks'
              }
              changeType="neutral"
            />
            <MetricCard
              label="Interest Earned"
              value={formatCurrency(interestEarned)}
              change={`${activeParticipations} active participation${activeParticipations === 1 ? '' : 's'}`}
              changeType="positive"
            />
          </>
        )}
      </MetricCardGrid>

      {position && (
        <Card className="mb-6 p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-sans text-tertiary mb-1">
                Network participant
              </div>
              <div className="text-sm font-sans text-primary">
                {position.institutionName ?? '--'}
              </div>
            </div>
            {position.charterNumber && (
              <div>
                <div className="text-xs font-sans text-tertiary mb-1">
                  NCUA charter
                </div>
                <div className="text-sm font-mono text-primary tabular-nums">
                  {position.charterNumber}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs font-sans text-tertiary mb-1">
                Last updated
              </div>
              <div className="text-sm font-sans text-secondary">
                {new Date(position.lastUpdatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 mb-6">
        <Heading level={2} size="card" className="mb-4">
          Subscribe / Redeem
        </Heading>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as 'subscribe' | 'redeem')}
          // Leaving Subscribe unmounts the wizard (its step state is lost) and
          // returning refetches its data, so arrow keys only move focus and
          // Enter or Space selects.
          activation="manual"
          className="mb-4"
        />
        {activeTab === 'subscribe' ? <SubscribeWizard /> : <RedeemPlaceholder />}
      </Card>

      <div className="mb-6">
        <Heading level={2} size="card" className="mb-3">
          Open subscriptions
        </Heading>
        <PendingSubscriptionsList
          subscriptions={openSubs}
          onOpenDetail={(sub) => setDetailSub(sub)}
        />
      </div>

      {historicalSubs.length > 0 && (
        <div className="mb-6">
          <Heading level={2} size="card" className="mb-3">
            Subscription history
          </Heading>
          <Card className="p-0 overflow-hidden">
            <TableScroll label="Subscription history">
            <table className="w-full min-w-table-dense text-sm font-sans">
              <thead className="bg-canvas/50 border-b border-recessed">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                    Funding Reference
                  </th>
                  <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
                    Amount
                  </th>
                  <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                    Status
                  </th>
                  <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                    Requested at
                  </th>
                  <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {historicalSubs.map((sub) => (
                  <tr
                    key={sub.requestId}
                    className="border-b border-recessed last:border-b-0"
                  >
                    <td className="px-4 py-3 font-mono tabular-nums text-primary">
                      {sub.fundingReference.value}
                    </td>
                    <td className="px-4 py-3 text-right text-primary">
                      {formatCurrencyFull(sub.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[sub.status]} size="sm">
                        {STATUS_LABEL[sub.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-tertiary">
                      {new Date(sub.requestedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setDetailSub(sub)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          </Card>
        </div>
      )}

      <SubscriptionDetailDialog
        subscription={detailSub}
        onClose={() => setDetailSub(null)}
      />
    </div>
  );
}
